<template>
  <div class="ai-chart-block">
    <!-- bar/pie/compare — существующие компоненты статистики -->
    <StatBarChart
      v-if="chartType === 'bar'"
      :title="title"
      :rows="singleRows"
      :value-formatter="valueFormatter"
      :height="barHeight"
    />
    <StatPieChart
      v-else-if="chartType === 'pie'"
      :title="title"
      :rows="singleRows"
      :value-formatter="valueFormatter"
    />
    <LogistsCompareChart
      v-else-if="chartType === 'compare'"
      :title="title"
      :rows="compareRows"
      :metrics="compareMetrics"
    />
    <!-- line / stacked_bar / nested_pie — generic echarts -->
    <div v-else-if="option" class="generic-chart">
      <div class="chart-title">{{ title }}</div>
      <VChart class="chart" :option="option" :style="{ height: height + 'px' }" autoresize />
    </div>
    <div v-else class="block-empty">Неподдерживаемый тип графика: {{ chartType }}</div>
  </div>
</template>

<script>
import { computed } from 'vue';
import VChart from '@/components/charts/echarts';
import StatBarChart from '@/pages/statistics/components/StatBarChart.vue';
import StatPieChart from '@/pages/statistics/components/StatPieChart.vue';
import LogistsCompareChart from '@/pages/statistics/components/LogistsCompareChart.vue';
import { formatterByName } from '../format';

const PALETTE = [
  '#007bff', '#12b76a', '#f79009', '#f04438', '#9e77ed',
  '#0ba5ec', '#e04f1f', '#66c2a5', '#f6c445', '#8d98ae',
  '#d6708e', '#475467',
];

export default {
  name: 'AiChartBlock',
  components: { VChart, StatBarChart, StatPieChart, LogistsCompareChart },
  props: {
    // block: {type: 'chart', chart: {chart_type, title, value_formatter,
    //   labels, series [{name, values}], metrics | inner_labels + metric}}
    block: { type: Object, required: true },
  },
  setup(props) {
    const chart = computed(() => props.block.chart || {});
    const chartType = computed(() => chart.value.chart_type);
    const title = computed(() => chart.value.title || '');
    const valueFormatter = computed(() =>
      formatterByName(chart.value.value_formatter)
    );

    // bar/pie: одна серия [{label, value}]
    const singleRows = computed(() => {
      const labels = chart.value.labels || [];
      const series = chart.value.series || [];
      const values = series.length ? series[0].values || [] : [];
      return labels.map((label, idx) => ({ label, value: values[idx] }));
    });

    const barHeight = computed(() =>
      Math.max(200, (chart.value.labels || []).length * 30 + 60)
    );

    // compare: rows [{logist, m0..}], metrics [{label, field, color, formatter}]
    const compareRows = computed(() => {
      const labels = chart.value.labels || [];
      const series = chart.value.series || [];
      return labels.map((label, idx) => {
        const row = { logist: label };
        series.forEach((s, sIdx) => {
          row[`m${sIdx}`] = (s.values || [])[idx];
        });
        return row;
      });
    });
    const compareMetrics = computed(() =>
      (chart.value.series || []).map((s, idx) => ({
        label: s.name,
        field: `m${idx}`,
        color: PALETTE[idx % PALETTE.length],
        formatter: valueFormatter.value,
      }))
    );

    // generic: line / stacked_bar / nested_pie
    const height = computed(() => {
      if (chartType.value === 'nested_pie') return 380;
      return Math.max(260, (chart.value.labels || []).length * 26 + 80);
    });

    const option = computed(() => {
      const type = chartType.value;
      const labels = chart.value.labels || [];
      const series = chart.value.series || [];
      const fmt = valueFormatter.value;
      if (type === 'line' || type === 'stacked_bar') {
        return {
          color: PALETTE,
          tooltip: {
            trigger: 'axis',
            confine: true,
            axisPointer: { type: type === 'line' ? 'line' : 'shadow' },
            valueFormatter: (v) => fmt(v),
          },
          legend: { top: 0, type: 'scroll', textStyle: { fontSize: 11 } },
          grid: { left: 8, right: 16, top: 32, bottom: 8, containLabel: true },
          xAxis: {
            type: 'category',
            data: labels,
            boundaryGap: type !== 'line',
            axisLabel: { fontSize: 11, color: '#475467' },
          },
          yAxis: {
            type: 'value',
            axisLabel: { formatter: (v) => fmt(v), fontSize: 11 },
          },
          series: series.map((s, idx) => ({
            name: s.name,
            type: type === 'line' ? 'line' : 'bar',
            smooth: type === 'line',
            stack: type === 'stacked_bar' ? 'total' : undefined,
            data: s.values || [],
            itemStyle: type === 'stacked_bar' ? { color: PALETTE[idx % PALETTE.length] } : undefined,
          })),
          dataZoom: labels.length > 60
            ? [{ type: 'inside' }, { type: 'slider', height: 16, bottom: 0 }]
            : undefined,
        };
      }
      if (type === 'nested_pie') {
        // labels — внешняя размерность (объекты), inner_labels — внутренняя.
        // Внутреннее кольцо — итого по объекту; внешнее — разбивка.
        const innerLabels = chart.value.inner_labels || [];
        const innerData = series.map((s, idx) => ({
          name: s.name,
          value: (s.values || []).reduce((sum, v) => sum + (Number(v) || 0), 0),
          isObject: true,
          itemStyle: { color: PALETTE[idx % PALETTE.length] },
        }));
        const outerData = [];
        series.forEach((s) => {
          (s.values || []).forEach((value, iIdx) => {
            outerData.push({
              name: innerLabels[iIdx],
              value: Number(value) || 0,
              object: s.name,
            });
          });
        });
        return {
          color: PALETTE,
          tooltip: {
            trigger: 'item',
            confine: true,
            formatter: (p) => {
              const value = fmt(p.value);
              if (p.data && p.data.object) {
                return `${p.marker} <b>${p.data.object}</b><br/>${p.name}: <b>${value}</b>`;
              }
              return `${p.marker} <b>${p.name}</b><br/>Итого: <b>${value}</b>`;
            },
          },
          legend: {
            orient: 'vertical',
            right: 0,
            top: 'middle',
            type: 'scroll',
            textStyle: { fontSize: 11, color: '#667085', width: 170, overflow: 'truncate' },
            tooltip: { show: true },
            data: innerLabels.map((name) => ({ name })),
          },
          series: [
            {
              type: 'pie',
              radius: ['26%', '48%'],
              center: ['30%', '50%'],
              label: { show: false },
              emphasis: { label: { show: false } },
              itemStyle: { borderColor: '#fff', borderWidth: 2 },
              data: innerData,
            },
            {
              type: 'pie',
              radius: ['52%', '76%'],
              center: ['30%', '50%'],
              label: { show: false },
              emphasis: { label: { show: false } },
              itemStyle: { borderColor: '#fff', borderWidth: 2 },
              data: outerData,
            },
          ],
        };
      }
      return null;
    });

    return {
      chartType,
      title,
      valueFormatter,
      singleRows,
      barHeight,
      compareRows,
      compareMetrics,
      option,
      height,
    };
  },
};
</script>

<style scoped>
.ai-chart-block {
  min-width: 0;
}

.generic-chart {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 12px 14px;
}

.chart-title {
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.chart {
  width: 100%;
}

.block-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  color: #98a2b3;
  font-size: 13px;
  border: 1px dashed #e5e7eb;
  border-radius: 8px;
}
</style>
